export const navLinks = [
  { name: "Home", path: "/" },
  { name: "Solutions", path: "/products" },
  { name: "About Us", path: "/about" },
  { name: "Careers", path: "/careers" },
  { name: "Contact", path: "/contact" },
];

export const capabilities = [
  {
    icon: "factory",
    title: "Smart Manufacturing",
    text: "IoT-instrumented production lines and digital twins deliver micron-level repeatability across every weld, cut, and assembly.",
  },
  {
    icon: "globe",
    title: "Global Logistics",
    text: "An integrated supply network and pre-commissioned modules enable rapid deployment to project sites on six continents.",
  },
  {
    icon: "leaf",
    title: "Sustainable R&D",
    text: "Low-emission drivetrains and energy-recovery systems engineered to exceed evolving international environmental standards.",
  },
  {
    icon: "cube",
    title: "Turnkey Solutions",
    text: "From concept to commissioning — design, fabrication, installation, and lifecycle support under one engineering roof.",
  },
];

export type ProductCategory =
  | "Batching Plants"
  | "Concrete Mixers"
  | "Construction Equipment"
  | "Material Handling";

export interface Product {
  name: string;
  category: ProductCategory;
  image: string;
  specs: string;
  detail: string;
}

export const slugify = (text: string) => 
  text.toLowerCase().replace(/[\s\W-]+/g, '-').replace(/^-+|-+$/g, '');

export const products: Product[] = [
  {
    name: "Mobile Batching Plant – Pan Mixer Type",
    category: "Batching Plants",
    image: "/images/Mobile Batching Plant – Pan Mixer Type.webp",
    specs: "Output 30–60 m³/h",
    detail: "Fully mobile batching plant with integrated pan mixer, PLC controls and rapid road-tow capability.",
  },
  {
    name: "Mobile Batching Plant",
    category: "Batching Plants",
    image: "/images/Mobile Batching Plant.webp",
    specs: "Output 30–90 m³/h",
    detail: "Versatile mobile concrete batching solution for remote and on-site project demands.",
  },
  {
    name: "Stationary Batching Plant",
    category: "Batching Plants",
    image: "/images/Stationary Batching Plant.webp",
    specs: "Output 90–120 m³/h",
    detail: "High-output stationary plant with twin silo feed, moisture compensation and automated weighing.",
  },
  {
    name: "Mechanical & Hydraulic Hopper Operated Mixer Machine",
    category: "Concrete Mixers",
    image: "/images/Mechanical & Hydraulic Hopper Operated Mixer.webp",
    specs: "Capacity 200–1000 L",
    detail: "Robust hopper-charged drum mixer for site-level concrete production with hydraulic lift option.",
  },
  {
    name: "Tractor Concrete Mixer",
    category: "Concrete Mixers",
    image: "/images/Tractor Concrete Mixer.webp",
    specs: "Capacity 500 L",
    detail: "PTO-driven tractor-mounted mixer designed for rural and remote construction sites.",
  },
  {
    name: "Transit Concrete Mixer",
    category: "Concrete Mixers",
    image: "/images/Transit Concrete Mixer.webp",
    specs: "Drum 6–10 m³",
    detail: "Road-legal ready-mix transit drum on heavy-duty truck chassis for urban concrete supply.",
  },
  {
    name: "Bar Cutting & Bending Machine",
    category: "Construction Equipment",
    image: "/images/Bar Cutting & Bending Machine.webp",
    specs: "Ø 8–40 mm rebar",
    detail: "Servo-driven shear and programmable multi-angle bending with alloy-steel die tooling.",
  },
  {
    name: "Canal Lining Paver",
    category: "Construction Equipment",
    image: "/images/Canal Lining Paver.webp",
    specs: "Width 600–2400 mm",
    detail: "Self-propelled paver for uniform concrete canal lining with adjustable profile screed.",
  },
  {
    name: "Cube Testing Machine",
    category: "Construction Equipment",
    image: "/images/Cube Testing Machine.webp",
    specs: "Capacity 2000 kN",
    detail: "Hydraulic compression testing machine for concrete cube and cylinder specimens per IS/BS/ASTM.",
  },
  {
    name: "Double Drum Winch",
    category: "Construction Equipment",
    image: "/images/Double Drum Winch.webp",
    specs: "Pull 5–50 T",
    detail: "Heavy-duty twin-drum winch for anchoring, pulling and lifting on civil and marine works.",
  },
  {
    name: "Piling Winch",
    category: "Construction Equipment",
    image: "/images/Piling Winch.webp",
    specs: "Pull 10–100 T",
    detail: "Diesel or electric-powered winch optimised for deep foundation and piling operations.",
  },
  {
    name: "Trimax System",
    category: "Construction Equipment",
    image: "/images/trimix-flooring-machine.webp",
    specs: "Custom config",
    detail: "Three-drum hoisting and compaction system for specialised substructure engineering works.",
  },
  {
    name: "Vibro Earth Compactor",
    category: "Construction Equipment",
    image: "/images/Vibro Earth Compactor.webp",
    specs: "Force 20–120 kN",
    detail: "Vibratory plate and drum compactors for soil, gravel and asphalt compaction on road works.",
  },
  {
    name: "Weigh Batcher",
    category: "Construction Equipment",
    image: "/images/Weigh Batcher.webp",
    specs: "Capacity 200–1000 L",
    detail: "Precision weighing batching unit for aggregate and cement in site-mix concrete production.",
  },
  {
    name: "Radial Well / Speres",
    category: "Construction Equipment",
    image: "https://images.pexels.com/photos/159306/construction-site-build-construction-work-159306.jpeg?auto=compress&cs=tinysrgb&w=800",
    specs: "Depth up to 30 m",
    detail: "Radial well boring and speres drilling equipment for dewatering and water well construction.",
  },
  {
    name: "Pipe Pushing Jack",
    category: "Construction Equipment",
    image: "/images/Pipe Pushing Jack.webp",
    specs: "Force 50–500 T",
    detail: "Hydraulic jack system for trenchless pipe-jacking and micro-tunnelling applications.",
  },
  {
    name: "Concrete Bucket",
    category: "Material Handling",
    image: "/images/Concrete Bucket.webp",
    specs: "Capacity 500–3000 L",
    detail: "Heavy-gauge crane-suspended concrete bucket with bottom-discharge gate for accurate placing.",
  },
  {
    name: "Construction Material Lift",
    category: "Material Handling",
    image: "/images/Construction Material Lift.webp",
    specs: "SWL 500–2000 kg",
    detail: "Mast-climbing material hoist for vertical transport of goods on high-rise construction sites.",
  },
  {
    name: "Grab",
    category: "Material Handling",
    image: "/images/grab.avif",
    specs: "Capacity 1–5 m³",
    detail: "Orange-peel and clamshell grab attachments for bulk material loading and excavation.",
  },
  {
    name: "Mobile Concrete Pump",
    category: "Material Handling",
    image: "/images/Mobile Concrete Pump.webp",
    specs: "Output 30–80 m³/h",
    detail: "Truck-mounted or trailer boom pump for high-rise and long-reach concrete placement.",
  },
];

export const productCategories: ProductCategory[] = [
  "Batching Plants",
  "Concrete Mixers",
  "Construction Equipment",
  "Material Handling",
];

export const productFilters = ["All", "Batching Plants", "Concrete Mixers", "Construction Equipment", "Material Handling"];

export const carouselProducts = products.slice(0, 6); // Or specific selection

export const projects = [
  {
    name: "Metro Viaduct Corridor",
    location: "Dubai, UAE",
    x: 60,
    y: 46,
    image: "https://images.pexels.com/photos/15532135/pexels-photo-15532135.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=627&w=1200&fm=webp",
    text: "Twelve stationary batching plants supplied for a 38 km elevated metro corridor, delivering 1.2M m³ of structural concrete.",
  },
  {
    name: "Coastal Highway Expansion",
    location: "Mumbai, India",
    x: 67,
    y: 50,
    image: "https://images.pexels.com/photos/35155685/pexels-photo-35155685.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=627&w=1200&fm=webp",
    text: "Fleet of mobile batching plants and CNC bar benders deployed for a 22 km sea-link infrastructure programme.",
  },
  {
    name: "Smart City District",
    location: "Singapore",
    x: 76,
    y: 56,
    image: "https://images.pexels.com/photos/31258538/pexels-photo-31258538.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=627&w=1200&fm=webp",
    text: "Turnkey precast yard with laser-guided handlers automating high-rise component production.",
  },
  {
    name: "Renewable Energy Park",
    location: "Berlin, Germany",
    x: 50,
    y: 35,
    image: "https://images.pexels.com/photos/37687676/pexels-photo-37687676.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=627&w=1200&fm=webp",
    text: "Sustainable batching solution supporting foundation works for a 400 MW wind installation.",
  },
];

export const certifications = [
  { code: "ISO 9001", label: "Quality Management" },
  { code: "ISO 14001", label: "Environmental Management" },
  { code: "ISO 45001", label: "Occupational Health & Safety" },
  { code: "CE", label: "European Conformity" },
];

export const team = [
  {
    name: "Dr. Anita Rao",
    role: "Head of R&D",
    image: "https://images.pexels.com/photos/3912378/pexels-photo-3912378.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=627&w=1200&fm=webp",
  },
  {
    name: "Marcus Lindqvist",
    role: "Chief Engineer",
    image: "https://images.pexels.com/photos/3912477/pexels-photo-3912477.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=627&w=1200&fm=webp",
  },
  {
    name: "Priya Mehta",
    role: "Automation Lead",
    image: "https://images.pexels.com/photos/8470779/pexels-photo-8470779.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=627&w=1200&fm=webp",
  },
  {
    name: "James Okoro",
    role: "Global Projects Director",
    image: "https://images.pexels.com/photos/6804068/pexels-photo-6804068.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=627&w=1200&fm=webp",
  },
];
